import { motion, AnimatePresence } from "framer-motion";
import React from "react";
import { useTranslation } from "react-i18next";
import { FaTrash, FaTimes } from "react-icons/fa";
import type { Branch } from "~/types/new_types";

interface ConfirmationModalProps {
  isOpen: boolean;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  theme?: Branch["theme"];
  isDangerous?: boolean;
  icon?: React.ReactNode;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  theme,
  isDangerous = false,
  icon,
}) => {
  const { t } = useTranslation();

  // Default theme values matching CartModal
  const defaultTheme = {
    primaryColor: "#ea580c",
    secondaryColor: "#f97316",
    backgroundColor: "#ffffff",
    textColor: "#1f2937",
    borderColor: "#d1d5db",
    borderRadius: "8px",
    fontFamily: "inherit",
    titleFontFamily: "inherit",
  };

  const safeTheme = theme || defaultTheme;

  // Default texts with translations
  const defaultTitle = title || t("menu.cart.confirmClearCart");
  const defaultMessage = message || t("menu.cart.confirmClearMessage");
  const defaultConfirmText = confirmText || t("menu.cart.yes");
  const defaultCancelText = cancelText || t("menu.cart.cancel");

  // Dangerous action colors
  const dangerousColor = "#EF4444";
  const confirmButtonColor = isDangerous ? dangerousColor : safeTheme.primaryColor;

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const modalVariants = {
    hidden: { scale: 0.9, opacity: 0, y: 20 },
    visible: {
      scale: 1,
      opacity: 1,
      y: 0,
    },
    exit: { scale: 0.9, opacity: 0, y: 20 },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={backdropVariants}
          className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={onCancel}
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: safeTheme.backgroundColor,
              color: safeTheme.textColor,
              borderRadius: safeTheme.borderRadius,
              border: `2px solid ${safeTheme.primaryColor || "#ea580c"}`,
              fontFamily: safeTheme.fontFamily,
            }}
          >
            {/* Header */}
            <div
              className="p-6 flex items-start justify-between"
              style={{
                backgroundColor: `${safeTheme.primaryColor}10`,
                borderBottom: `2px solid ${safeTheme.primaryColor}`,
              }}
            >
              <div className="flex items-start gap-4 flex-1">
                {/* Icon */}
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: isDangerous
                      ? "#FEE2E2"
                      : `${safeTheme.primaryColor}20`,
                  }}
                >
                  {icon ? (
                    icon
                  ) : (
                    <FaTrash
                      size={20}
                      style={{
                        color: isDangerous
                          ? dangerousColor
                          : safeTheme.primaryColor,
                      }}
                    />
                  )}
                </div>

                {/* Title - vertically centered */}
                <div className="flex items-center h-12">
                  <h2
                    className="text-base font-medium w-full text-center"
                    style={{ color: safeTheme.textColor }}
                  >
                    {defaultTitle}
                  </h2>
                </div>
              </div>

              {/* Close button */}
              <button
                onClick={onCancel}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer"
                style={{
                  backgroundColor: `${safeTheme.primaryColor}20`,
                  color: safeTheme.primaryColor,
                }}
              >
                <FaTimes size={14} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p
                className="text-base leading-relaxed font-medium"
                style={{ color: `${safeTheme.textColor}B3` }}
              >
                {defaultMessage}
              </p>
            </div>

            {/* Actions */}
            <div
              className="p-6 flex gap-3"
              style={{
                backgroundColor: safeTheme.backgroundColor,
                borderTop: `1px solid ${safeTheme.primaryColor}20`,
              }}
            >
              <motion.button
                onClick={onCancel}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 py-3 px-4 rounded-lg font-semibold transition-all cursor-pointer"
                style={{
                  backgroundColor: "#ffffff",
                  color: safeTheme.textColor,
                  borderRadius: safeTheme.borderRadius,
                  border: `1px solid ${safeTheme.primaryColor || "#ea580c"}`,
                }}
              >
                {defaultCancelText}
              </motion.button>

              <motion.button
                onClick={onConfirm}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 py-3 px-4 rounded-lg font-semibold text-white transition-all cursor-pointer flex items-center justify-center gap-2"
                style={{
                  backgroundColor: confirmButtonColor,
                  borderRadius: safeTheme.borderRadius,
                  border: `1px solid ${confirmButtonColor}`,
                  boxShadow: `0 4px 12px ${confirmButtonColor}30`,
                }}
              >
                <FaTrash size={14} />
                {defaultConfirmText}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConfirmationModal;
